import { useMemo } from 'react';
import { Row, StyledActionBtn } from '../styles';
import { Wrapper, TextfieldsWrapper, ButtonsWrapper, TableTitle, EditModuleBtn, SubtypeModuleFamily, Label, CheckboxWrapper } from './styles';
import HistoryTextfield from './HistoryTextfield';
import ScoringTable from './ScoringTable';
import { moduleStatuses } from '../enums';
import { orderBy } from 'lodash';
import Checkbox from '../../Checkbox';

const transformScoringTableRow = (scoringTable) => ({
  score: scoringTable?.totalScore ?? '-',
  reasoning: scoringTable?.totalReasoning || '',
  phase: scoringTable?.phase || 1,
});

const ModuleSubcomponent = ({
  module,
  onEditModule,
  updateModule,
  onSaveModule,
  onScoringCompleted,
  onRejectScoring,
  isAdmin,
  projectPhase,
  projectPhaseName,
}) => {
  const projectModule = useMemo(() => module, [module]);
  const updateScoringCellData = (groupIndex, rowIndexInGroup, rowIndex, columnId, value) => {
    const scoringTable = { ...projectModule.scoringTable };

    const group = scoringTable.groups[groupIndex];

    const rows = group.rows;

    const updatedRow = { ...rows[rowIndexInGroup], [columnId]: value };

    rows.splice(rowIndexInGroup, 1, updatedRow);

    const updatedModule = { ...projectModule, scoringTable };

    updateModule(updatedModule);
  };

  const setScoringTablePrimitiveField = (key, value) => {
    const updatedModule = { ...projectModule, scoringTable: { ...projectModule.scoringTable, [key]: value } };
    updateModule(updatedModule);
  };

  const historyTables = useMemo(() => {
    if (!projectModule.scoringTableHistory) return [];
    return projectModule.scoringTableHistory.map((historyTable) => historyTable.groups.flatMap((group) => group.rows));
  }, []);

  const totalRowHistoryRows = useMemo(() => {
    if (!projectModule.scoringTableHistory) return [];
    return projectModule.scoringTableHistory.map((historyTable) => transformScoringTableRow(historyTable));
  }, []);

  const totalRow = useMemo(() => {
    return {
      ...transformScoringTableRow(projectModule?.scoringTable),
      score: projectModule?.siScore ?? 0,
      historyRows: totalRowHistoryRows,
    };
  }, [projectModule.scoringTable]);

  const intentHistory = useMemo(() => {
    return (
      orderBy(
        projectModule.scoringTableHistory?.map((h) => ({
          phase: h.phase,
          phaseName: h.phaseName,
          value: h.intent,
        })),
        'phase'
      ) || []
    );
  }, [projectModule.scoringTableHistory]);

  const controlIntentHistory = useMemo(() => {
    return (
      orderBy(
        projectModule.scoringTableHistory?.map((h) => ({
          phase: h.phase,
          phaseName: h.phaseName,
          value: h.controlIntent,
        })),
        'phase'
      ) || []
    );
  }, [projectModule.scoringTableHistory]);

  const commentHistory = useMemo(() => {
    return (
      orderBy(
        projectModule?.scoringTableHistory?.map((h) => ({
          phase: h.phase,
          phaseName: h.phaseName,
          value: h.comment,
        })),
        'phase'
      ) || []
    );
  }, [projectModule.scoringTableHistory]);

  const activePhase = {
    phase: projectPhase,
    phaseName: projectPhaseName,
  };

  if (!projectModule?.scoringTable) return null;
  const completeDisabled = !(projectPhase === 1) && projectModule.scoringTable?.groups?.filter(g => g.rows?.filter(r => !Number.isInteger(r.score)).length > 0).length > 0;
  return (
    <Wrapper>
      <Row
        justify='flex=start'
        marginBottom='0.375rem'>
        <TableTitle>Scoring | {projectModule.name}</TableTitle>
        {isAdmin && (
          <EditModuleBtn
            title='Edit Module'
            onClick={onEditModule}
          />
        )}
      </Row>
      <Row marginBottom='1.5rem'>
        <SubtypeModuleFamily>
            Family: {projectModule.family + ' - ' + projectModule.tsCode}
            <span style={{marginLeft:'50px'}}>Secondary system: {projectModule.secondarySystem}</span>
        </SubtypeModuleFamily>
      </Row>
      {projectPhase !== 1 && (
        <ScoringTable
          groups={projectModule.scoringTable?.groups}
          historyTables={historyTables}
          totalRow={totalRow}
          updateScoringCellData={updateScoringCellData}
          setScoringTablePrimitiveField={setScoringTablePrimitiveField}
          isAdmin={isAdmin}
          isBespokeModule={projectModule.bespoke}
        />
      )}
      <TextfieldsWrapper>
        {projectModule.bespoke && <CheckboxWrapper><Checkbox
            classes='checkbox'
            value={projectModule.repeatable}
            onChange={(newVal) => {}}/><Label>Repeatable</Label></CheckboxWrapper>
        }
        <HistoryTextfield
          activePhase={{ ...activePhase, value: projectModule?.scoringTable.intent }}
          history={intentHistory}
          label={`${projectModule?.bespoke ? 'Bespoke' : 'Standardization'} Intent`}
          disabled={!isAdmin}
          onBlur={(newValue) => setScoringTablePrimitiveField('intent', newValue)}
        />
        <HistoryTextfield
          activePhase={{ ...activePhase, value: projectModule?.scoringTable?.intentControl }}
          history={controlIntentHistory}
          label='What must be controlled to meet the intent'
          disabled={!isAdmin}
          onBlur={(newValue) => setScoringTablePrimitiveField('intentControl', newValue)}
        />
        <HistoryTextfield
          activePhase={{ ...activePhase, value: projectModule?.scoringTable.comment }}
          history={commentHistory}
          label='Comments'
          disabled={!isAdmin}
          onBlur={(newValue) => setScoringTablePrimitiveField('comment', newValue)}
        />
      </TextfieldsWrapper>
      {isAdmin && (
        <ButtonsWrapper>
          <StyledActionBtn
            title='Save'
            onClick={() => onSaveModule(projectModule)}
          />
          {projectModule.status !== moduleStatuses.COMPLETED.value && (
            <StyledActionBtn
              // disabled={projectModule.isInCreation}
              width='250px'
              title='Mark Scoring Completed'
              onClick={() => onScoringCompleted(projectModule)}
              disabled={!(projectModule?.siScore && projectModule?.referenceModuleName && !completeDisabled)}
            />
          )}
          {/* {!projectModule.isInCreation && projectModule.status === moduleStatuses.COMPLETED.value && ( */}
          {!projectModule.bespoke && projectModule.status === moduleStatuses.COMPLETED.value && (
            <StyledActionBtn
              width='250px'
              title='Reject'
              wrapperStyle={{ backgroundColor: 'firebrick' }}
              onClick={() => onRejectScoring(projectModule)}
            />
          )}
        </ButtonsWrapper>
      )}
    </Wrapper>
  );
};
export default ModuleSubcomponent;
